import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { reportApi, clientApi } from '@/services/api'
import type { CashSummary as CashSummaryType } from '@/types'
import { formatCurrency } from '@/utils/currency'
import { CreditCard, ArrowDownCircle, ArrowUpCircle, Wallet, Download } from 'lucide-react'
import toast from 'react-hot-toast'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { Separator } from '@/components/ui/separator'

export default function CashSummary() {
  const { clientId } = useParams<{ clientId: string }>()
  const [report, setReport] = useState<CashSummaryType | null>(null)
  const [loading, setLoading] = useState(false)
  const [startDate, setStartDate] = useState(() => `${new Date().getFullYear()}-01-01`)
  const [endDate, setEndDate] = useState(() => new Date().toISOString().split('T')[0])
  const [exporting, setExporting] = useState(false)
  const [currency, setCurrency] = useState('USD')

  useEffect(() => {
    if (clientId) clientApi.get(clientId).then((r) => setCurrency(r.data.client.currency)).catch(() => {})
    generate()
  }, [clientId])

  async function generate() {
    if (!clientId) return
    setLoading(true)
    try {
      const res = await reportApi.cashSummary(clientId, startDate, endDate)
      setReport(res.data)
    } finally { setLoading(false) }
  }

  async function handleExport() {
    if (!clientId) return
    setExporting(true)
    try {
      const res = await reportApi.exportPdf(clientId, 'cash-summary', { start_date: startDate, end_date: endDate })
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }))
      const a = document.createElement('a')
      a.href = url
      a.download = `cash-summary-${startDate}-to-${endDate}.pdf`
      a.click()
      URL.revokeObjectURL(url)
    } catch {
      toast.error('Export failed')
    } finally {
      setExporting(false)
    }
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-[17px] font-bold text-[#000A1E] leading-[24px]">Cash Summary</h1>
          <p className="text-[13px] text-[rgba(0,10,30,0.75)] mt-0.5">Cash movements for the period</p>
        </div>
        {report && (
          <Button variant="outline" onClick={handleExport} disabled={exporting}>
            <Download className="w-4 h-4" />
            {exporting ? 'Exporting...' : 'Export PDF'}
          </Button>
        )}
      </div>

      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-end gap-3">
            <div className="space-y-1.5">
              <Label>From</Label>
              <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="w-auto" />
            </div>
            <div className="space-y-1.5">
              <Label>To</Label>
              <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="w-auto" />
            </div>
            <Button onClick={generate} disabled={loading}>
              {loading ? 'Generating...' : 'Generate'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <Card className="max-w-lg">
          <CardContent className="p-5 space-y-3">
            {[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-14 w-full" />)}
          </CardContent>
        </Card>
      ) : report ? (
        <Card className="max-w-lg">
          <CardContent className="p-5">
            <p className="text-[13px] text-[rgba(0,10,30,0.75)] mb-4">{report.start_date} to {report.end_date}</p>

            <div className="space-y-1">
              <div className="flex items-center justify-between py-3">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 bg-muted rounded-lg flex items-center justify-center">
                    <Wallet className="w-4 h-4 text-[rgba(0,10,30,0.75)]" />
                  </div>
                  <span className="text-[13px] text-[#000A1E]">Opening Balance</span>
                </div>
                <span className="text-[13px] font-bold text-[#000A1E] tabular-nums">{formatCurrency(report.opening_balance, currency)}</span>
              </div>

              <Separator />

              <div className="flex items-center justify-between py-3">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 bg-emerald-50 rounded-lg flex items-center justify-center">
                    <ArrowDownCircle className="w-4 h-4 text-emerald-500" />
                  </div>
                  <span className="text-[13px] text-[#000A1E]">Total Received</span>
                </div>
                <span className="text-[13px] font-bold text-emerald-700 tabular-nums">+{formatCurrency(report.total_received, currency)}</span>
              </div>

              <div className="flex items-center justify-between py-3">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 bg-red-50 rounded-lg flex items-center justify-center">
                    <ArrowUpCircle className="w-4 h-4 text-red-500" />
                  </div>
                  <span className="text-[13px] text-[#000A1E]">Total Spent</span>
                </div>
                <span className="text-[13px] font-bold text-red-700 tabular-nums">-{formatCurrency(report.total_spent, currency)}</span>
              </div>

              <Separator className="border-t-2" />

              <div className="flex items-center justify-between py-3">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 bg-[#0078C8]/10 rounded-lg flex items-center justify-center">
                    <CreditCard className="w-4 h-4 text-[#0078C8]" />
                  </div>
                  <span className="text-base font-bold text-[#000A1E]">Closing Balance</span>
                </div>
                <span className="text-lg font-bold text-[#000A1E] tabular-nums">{formatCurrency(report.closing_balance, currency)}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card className="max-w-lg">
          <CardContent className="p-12 text-center">
            <CreditCard className="w-8 h-8 text-[#CCCED2] mx-auto mb-3" />
            <p className="text-[13px] text-[rgba(0,10,30,0.75)]">Select a date range and click Generate</p>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
